'use client';
import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useConvex } from 'convex/react';
import { useAuth } from '@/lib/auth';
import { clearBrowserNavigationDraft } from '@/components/home/design-composer/navigation-draft-checkpoint';
import { deliverDesignNavigation } from '@/components/home/design-composer/navigation-delivery';
import { createNavigationDeliveryStore } from '@/components/home/design-composer/navigation-delivery-store';
import {
  readNavigationDeliveries,
  writeNavigationDeliveries,
} from '@/components/home/design-composer/navigation-delivery-storage';
import { useNavigationDeliveryReconciliation } from './use-navigation-delivery-reconciliation';

const Context = createContext<ReturnType<
  typeof createNavigationDeliveryStore
> | null>(null);
export function NavigationDeliveryProvider({
  children,
}: {
  children: ReactNode;
}) {
  const client = useConvex();
  const { user } = useAuth();
  const actorRef = useRef(user?.userId);
  useEffect(() => {
    actorRef.current = user?.userId;
  }, [user?.userId]);
  // Construction only captures the runner; the actor ref is read during async delivery.
  // eslint-disable-next-line react-hooks/refs
  const [store] = useState(() =>
    createNavigationDeliveryStore(
      (job, checkpoint) =>
        deliverDesignNavigation(job, client, checkpoint, () => {
          if (actorRef.current !== job.actorId)
            throw new Error('Delivery actor changed');
        }),
      (job) => clearBrowserNavigationDraft(job.sourceDraftCheckpoint)
    )
  );
  useEffect(() => {
    try {
      const storage = window.sessionStorage;
      store.restore(readNavigationDeliveries(storage));
      return store.subscribe(() =>
        writeNavigationDeliveries(storage, store.getSnapshot())
      );
    } catch {
      return undefined;
    }
  }, [store]);
  useNavigationDeliveryReconciliation(store, user?.userId);
  return <Context.Provider value={store}>{children}</Context.Provider>;
}
export function useNavigationDeliveryStore() {
  return useContext(Context);
}
const empty: ReturnType<
  ReturnType<typeof createNavigationDeliveryStore>['getSnapshot']
> = [];
const noopSubscribe = () => () => {};
const emptySnapshot = () => empty;
export function useNavigationDeliveries() {
  const store = useNavigationDeliveryStore();
  const entries = useSyncExternalStore(
    store?.subscribe ?? noopSubscribe,
    store?.getSnapshot ?? emptySnapshot,
    emptySnapshot
  );
  return { entries, store };
}
